import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useCurrency } from "../CurrencyContext";
import { useSettings } from "../SettingsContext";
import { CATEGORY_ORDER, CATEGORY_IMG, resolveSiteImage, catLabel } from "../brand";
import EditableText from "../components/EditableText";
import EditableImage from "../components/EditableImage";
import EditableI18n from "../components/EditableI18n";
import { translations } from "../i18n";

const T_EN = translations.en;
const T_FR = translations.fr;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Services() {
  const { t, lang } = useLang();
  const { format } = useCurrency();
  const { settings } = useSettings();
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.services || [])).catch(() => {});
  }, []);

  return (
    <main data-testid="services-page" className="pt-32">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-24">
        <p className="qw-overline mb-6">
          <EditableText kEn="services_overline_en" kFr="services_overline_fr" label="Services overline"
            fallback={{ en: T_EN.services.overline, fr: T_FR.services.overline }} />
        </p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-foreground text-balance mb-8 max-w-4xl">
          <EditableText kEn="services_title_en" kFr="services_title_fr" label="Services page title"
            fallback={{ en: T_EN.services.title, fr: T_FR.services.title }} />
        </h1>
        <p className="text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          <EditableText kEn="services_intro_en" kFr="services_intro_fr" label="Services intro"
            fallback={{ en: T_EN.services.subtitle, fr: T_FR.services.subtitle }} />
        </p>
      </section>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      {/* Categories */}
      {CATEGORY_ORDER.map((cat, idx) => {
        const list = services.filter((s) => s.category === cat);
        if (list.length === 0) return null;
        const reverse = idx % 2 === 1;
        return (
          <section
            key={cat}
            data-testid={`services-category-${cat}`}
            className="px-6 md:px-12 lg:px-20 py-20 md:py-28 border-b border-border"
          >
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start ${reverse ? "lg:[direction:rtl]" : ""}`}>
              <div className={`lg:col-span-5 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                <div className="lg:sticky lg:top-32">
                  <p className="qw-overline mb-4">
                    0{idx + 1} / 0{CATEGORY_ORDER.length}
                  </p>
                  <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-foreground tracking-tight mb-8">
                    <EditableText kEn={`cat_${cat}_label_en`} kFr={`cat_${cat}_label_fr`} label="Category name"
                      fallback={{ en: T_EN.services.categories[cat], fr: T_FR.services.categories[cat] }} />
                  </h2>
                  <div className={`relative overflow-hidden qw-grain ${cat === "lashes" ? "aspect-[3/2]" : "aspect-[4/5]"}`}>
                    <EditableImage imageKey={cat} label="Photo" />
                    <img
                      src={resolveSiteImage(settings, cat) || CATEGORY_IMG[cat]}
                      alt={catLabel(settings, lang, cat, t.services.categories[cat])}
                      style={{ objectPosition: cat === "lashes" ? "50% 28%" : "center" }}
                      className="w-full h-full object-cover qw-ken-burns hover:scale-110 transition-transform duration-[1800ms] ease-out"
                    />
                  </div>
                </div>
              </div>

              <div className={`lg:col-span-7 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                <ul className="divide-y divide-border border-y border-border">
                  {list.map((s) => (
                    <li
                      key={s.id}
                      data-testid={`service-row-${s.id}`}
                      className="py-8 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline hover:bg-muted/30 transition-colors px-4 md:px-6 -mx-4 md:-mx-6"
                    >
                      <div className="md:col-span-7">
                        <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-2">
                          {lang === "fr" ? s.name_fr : s.name_en}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {lang === "fr" ? s.effect_fr : s.effect_en}
                        </p>
                      </div>
                      <div className="md:col-span-3 flex items-center gap-2 text-xs text-muted-foreground tracking-wider uppercase">
                        {s.duration !== "—" && (
                          <>
                            <Clock size={12} strokeWidth={1.2} className="text-primary" />
                            <span><EditableI18n k="services.duration" label="Duration label" />: {s.duration}</span>
                          </>
                        )}
                      </div>
                      <div className="md:col-span-2 md:text-right">
                        <p className="qw-num font-serif text-2xl text-primary">
                          {s.price_fcfa === 0 ? t.services.priceOnConsult : format(s.price_fcfa)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-10">
                  <Link
                    to={`/booking?service=${list[0]?.id || ""}`}
                    data-testid={`book-category-${cat}`}
                    className="qw-btn qw-btn-outline"
                  >
                    <EditableI18n k="services.bookThis" label="Book this treatment link" /> <ArrowUpRight size={14} strokeWidth={1.2} />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Deposit note */}
      <section className="px-6 md:px-12 lg:px-20 py-16">
        <p className="text-center text-xs text-muted-foreground tracking-[0.2em] uppercase max-w-2xl mx-auto">
          <EditableI18n k="deposit.note" label="Deposit note" />
        </p>
      </section>
    </main>
  );
}
